import escape from 'lodash/escape';

import { prismFormatJSON } from 'utils/strings';

import Events from 'app/collections/events';
import DateRange, { DATERANGE_PARAM_TYPES } from 'app/models/date_range';
import DropdownSelection from 'app/models/dropdown_selection';
import EventDescriptionDropdownSelection from 'app/models/event_description_dropdown_selection';
import EventPayloads from 'app/collections/event_payloads';

import Base from 'app/views/base';
import DatepickerRangeView from 'app/views/datepicker_range_view';
import LoaderView from 'app/views/loader_view';
import SelectorView from 'app/views/selector_view';

import DateAndTimeHeadingView from 'app/components/list/item/headings/date_and_time';
import PageHeaderView from 'app/components/page_header';
import { List as ListView, Heading } from 'app/components/list/list';
import ListSearchView from 'app/components/list/search';

import tmpl from './template';
import eventStatusHeadingTemplate from './event_status_heading';
import eventDescriptionDisplayHeadingTemplate from './event_description_display_heading';
import eventPayloadsTemplate from './event_payloads';


export const EventStatusHeadingView = Base.extend({
  compiledTemplate: eventStatusHeadingTemplate,

  context() {
    const status = this.model.get('status');
    return {
      status,
      statusClass: this.getStatusClass(status),
    };
  },

  getStatusClass: function(status) {
    switch(status) {
      case 'completed': return 'glyphicon-ok text-green';
      case 'failed': return 'glyphicon-remove text-red';
      default: return 'glyphicon-time text-yellow';
    }
  }
});

export const DescriptionHeadingDisplayView = Base.extend({
  compiledTemplate: eventDescriptionDisplayHeadingTemplate,
});

export const EventPayloadsView = Base.extend({
  compiledTemplate: eventPayloadsTemplate,

  initialize() {
    this.collection = new EventPayloads([], { event: this.model });

    const loaderName = `${this.model.get('id')}-loader`;
    this.addSubview(loaderName, `#${loaderName}`, new LoaderView({ collection: this.collection }));
    this.listenTo(this.collection, 'sync error', this.render);

    this.collection.fetch();
  },

  context() {
    return {
      model: this.model,
      eventId: this.model.get('id'),
      firstFetchSuccess: this.collection.firstFetchSuccess,
      collection: this.collection,
      parseAndFormatJSON: this.parseAndFormatJSON,
      getResponseCodeColor: this.getResponseCodeColor,
    };
  },

  parseAndFormatJSON(jsonString) {
    try {
      return prismFormatJSON(JSON.parse(jsonString));
    } catch (e) {
      return escape(jsonString);
    }
  },

  getResponseCodeColor(code) {
    return (code >= 200 && code <= 299) ? 'green' : 'red';
  },
});

export const buildHeadings = () => {
  return [
    new Heading({
      subView: m => new EventStatusHeadingView({ model: m }),
      display: 'Status',
      xsSize: 2,
      smSize: 2,
    }),
    new Heading({
      property: 'description',
      displayView: new DescriptionHeadingDisplayView({}),
      xsSize: 2,
      smSize: 2,
      wrapperClass: m => m ? 'monospace-font' : '',
    }),
    new Heading({
      property: 'id',
      display: 'ID',
      xsSize: 4,
      smSize: 4,
    }),
    new Heading({
      subView: m => new DateAndTimeHeadingView({ model: m }),
      display: 'Date',
      smSize: 2,
      xsSize: 2,
      wrapperClass: 'text-nowrap',
    }),
  ];
};

export const buildShowDetails = () => {
  return {
    DetailsView: EventPayloadsView,
  };
};


export default Base.extend({
  compiledTemplate: tmpl,
  className: 'events',

  initialize({ apiModeSelection }) {
    this.apiModeSelection = apiModeSelection;
    this.dateRange = new DateRange({ paramType: DATERANGE_PARAM_TYPES.DATETIME });
    this.statusSelection = new DropdownSelection({
      choices: [
        { option: '', readable: 'All Statuses' },
        { option: true, readable: 'Failed' },
      ],
      label: 'Status',
      paramKey: 'failed',
    });
    this.descriptionSelection = new EventDescriptionDropdownSelection({});
    this.collection = new Events([], {
      apiModeSelection: this.apiModeSelection,
      dateRange: this.dateRange,
      statusSelection: this.statusSelection,
      descriptionSelection: this.descriptionSelection,
    });

    this.addSubview('header', 'header', new PageHeaderView({
      name: 'Events',
      links: [
        { name: 'Docs', href: '/docs/api#events' },
        { name: 'Guide', href: '/webhooks-guide' },
      ],
    }));
    this.addSubview('apiModeSelector', '#api_mode_selector', new SelectorView({ model: this.apiModeSelection }));
    this.addSubview('datepickers', '#events_daterange_pickers', new DatepickerRangeView({ model: this.dateRange }));
    this.addSubview('eventsStatusSelector', '#events_status_selector', new SelectorView({ model: this.statusSelection }));
    this.addSubview('eventsDescriptionSelector', '#events_description_selector', new SelectorView({ model: this.descriptionSelection }));
    this.addSubview('search', '#event_search', new ListSearchView({
      collection: this.collection, placeholder: '(Event ID)',
    }));
    this.addSubview('eventList', '#event_list', new ListView({
      headings: buildHeadings(),
      collection: this.collection,
      noResultsMessage: 'No events have been found matching your filter set.',
      showDetails: buildShowDetails(),
    }));
  },
});



// WEBPACK FOOTER //
// ./app/components/pages/webhooks_and_events/events/index.js